import React, { useCallback, useEffect, useState } from 'react';
import { Flex, Input, List } from 'antd';
import { Link } from 'react-router-dom';
import { CustomPagination, CustomTitle } from '../../../components';
import { ChevronRightIcon, SearchIcon } from '../../../assets/icons/icons';
import { useSpin } from '@/components/SpinContext.jsx';
import { Airport, getAllAirports } from '@/apis/airports';
import { useUnAuthorize } from '@/hooks';

/**
 * AirportList Component
 *
 * A searchable list of airports loaded from JSON data.
 * Allows users to filter airports by IATA code or city name
 * and navigate to individual airport pages.
 */
const AirportList: React.FC = () => {
  const { showSpin, hideSpin } = useSpin();
  const { unauthorize } = useUnAuthorize();
  const [airportData, setAirportData] = useState<Airport[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchAirportList = useCallback(() => {
    if (!searchTerm) {
      showSpin();
      setIsLoading(true);
    }

    const pageToSend = searchTerm ? 1 : page;
    getAllAirports(searchTerm, pageToSend)
      .then((res) => {
        const data = res?.data;
        if (res.is_success) {
          if (data && Array.isArray(data)) {
            setAirportData(data);
          }
          setTotalPages(res?.page_count ?? 0);
        } else {
          setAirportData([]);
        }
      })
      .catch((error) => {
        unauthorize(error);
        setAirportData([]);
      })
      .finally(() => {
        hideSpin();
        setIsLoading(false);
      });
  }, [unauthorize, searchTerm, page, showSpin, hideSpin]);

  const handlePageChange = (newPage: number) => {
    setPage(() => {
      const validPage = newPage > 0 ? newPage : 1;
      return validPage;
    });
  };
  useEffect(() => {
    fetchAirportList();
  }, [fetchAirportList, searchTerm]);

  return (
    <>
      <section className='airport-list'>
        <Flex vertical gap={20}>
          <CustomTitle
            className='zl-custom-title'
            text='Airports'
            fontSize='28px'
            fontWeight={700}
            lineHeight='36px'
            marginBottom='0'
          />
          <Flex vertical gap={1}>
            <Flex vertical gap={20}>
              <Input
                className='zl-search'
                placeholder='Search for an airport...'
                prefix={
                  <SearchIcon
                    fill='var(--text-primary)'
                    width={12}
                    height={12}
                  />
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <CustomTitle
                text='Most searched'
                fontSize='12px'
                fontWeight={600}
                lineHeight='16px'
                color='var(--text-secondary)'
              />
            </Flex>

            {/* Loading State */}
            {isLoading && (
              <Flex
                justify='center'
                align='center'
                style={{ padding: '40px 0' }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '16px' }}>Loading...</div>
                </div>
              </Flex>
            )}

            {/* Data List */}
            {!isLoading && (
              <List
                className='zl-airport-list'
                dataSource={airportData}
                renderItem={(item: Airport) => (
                  <List.Item>
                    <Link
                      to={`/airport-list/airport/${item.iata_code}`}
                      state={{ airport: item }}
                      style={{
                        width: '100%',
                        textDecoration: 'none',
                        color: 'inherit',
                      }}
                    >
                      <Flex
                        align='center'
                        justify='space-between'
                        style={{ width: '100%' }}
                      >
                        <span>
                          {item?.name}
                          {item?.iata_code?.trim()
                            ? ` (${item?.iata_code})`
                            : ''}
                        </span>
                        <ChevronRightIcon
                          fill='var(--text-secondary)'
                          width={24}
                          height={24}
                        />
                      </Flex>
                    </Link>
                  </List.Item>
                )}
              />
            )}
          </Flex>
        </Flex>
      </section>
      {!isLoading && airportData?.length > 0 && (
        <Flex justify='center' align='center' className='w-100'>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CustomPagination
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </Flex>
      )}
    </>
  );
};

export default AirportList;
